import { pdf } from '@react-pdf/renderer';
import axios from 'axios';
import React from 'react';
import { API_BASE_URL as API_BASE } from '../config/apiConfig';
import AnalysisReportPDF from './AnalysisReportPDF';

export const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch (e) {
        return "N/A";
    }
};

const mapSnapshotKey = (analysis, suffix = "") => {
    const base = analysis.id 
        ? `developmentAnalyzerMapSnapshot:id:${analysis.id.toString()}` 
        : `developmentAnalyzerMapSnapshot:${(analysis.apn || analysis.address || "property").toString()}`;
    return suffix ? `${base}:${suffix}` : base;
};

export const getStoredMapSnapshot = (analysis, suffix = "") => {
    try {
        const key = mapSnapshotKey(analysis, suffix);
        const raw = sessionStorage.getItem(key) || localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.dataUrl ? parsed : null;
    } catch (error) {
        return null;
    }
};

const fetchReportEvidence = async (analysis) => {
    const params = {
        address: analysis.address,
        apn: analysis.apn,
        city: analysis.city_code,
        analysis_id: analysis.id, // Ensure we use the cached analysis
    };

    const storedSnapshot = getStoredMapSnapshot(analysis);
    const storedSnapshotNoContours = getStoredMapSnapshot(analysis, "no-contours");

    const [developmentTrace, demContours, aerialSnapshot, aerialSnapshotNoContours] = await Promise.allSettled([
        axios.get(`${API_BASE}/api/parcel-development-area`, {
            timeout: 60000,
            params: { ...params, includeGeometry: false },
        }),
        axios.get(`${API_BASE}/api/parcel-dem-contours`, {
            timeout: 120000,
            params: {
                ...params,
                interval_ft: analysis.contour_interval_selected_ft || 2,
                grid_spacing_ft: 8,
                includeGeometry: false,
            },
        }),
        storedSnapshot ? Promise.resolve(storedSnapshot) : Promise.resolve(null),
        storedSnapshotNoContours ? Promise.resolve(storedSnapshotNoContours) : Promise.resolve(null),
    ]);

    return {
        developmentTrace: developmentTrace.status === "fulfilled" ? developmentTrace.value.data : null,
        demContours: demContours.status === "fulfilled" ? demContours.value.data : null,
        aerialSnapshot: aerialSnapshot.status === "fulfilled" ? aerialSnapshot.value : null,
        aerialSnapshotNoContours: aerialSnapshotNoContours.status === "fulfilled" ? aerialSnapshotNoContours.value : null,
    };
};

export const exportAnalysisToPDF = async (analysis) => {
    console.log("🚀 Redesigned PDF Export Triggered", { analysis });
    if (!analysis) return;

    try {
        const evidence = await fetchReportEvidence(analysis).catch((error) => {
            console.warn("Report evidence fetch failed:", error);
            return { developmentTrace: null, demContours: null, aerialSnapshot: null };
        });

        if (evidence.aerialSnapshot) {
            console.log("✅ PDF Export: Map snapshot found. Including 'Site Context Map' page.");
        } else {
            console.log("ℹ️ PDF Export: No map snapshot found. Map page will be omitted.");
        }

        const formattedDate = formatDate(new Date());

        // Generate the PDF blob using the professional component
        const blob = await pdf(
            <AnalysisReportPDF 
                analysis={analysis} 
                evidence={evidence} 
                formattedDate={formattedDate} 
            />
        ).toBlob();

        // Create a download link and trigger it
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const filename = analysis.address
            ? `Feasibility_Report_${String(analysis.address).replace(/[^\w]+/g, '_').substring(0, 48)}.pdf`
            : 'Property_Feasibility_Report.pdf';
            
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
    } catch (error) {
        console.error("PDF Generation Error:", error);
        throw error;
    }
};

export default exportAnalysisToPDF;
